import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// Milestone phrases - calm, observational, not celebratory
const MILESTONE_PHRASES = {
  3: "Momentum feels different.",
  9: "This is flow.",
  18: "This is who you are when you stay steady."
};

const CompletionAcknowledgment = ({ completedCount, sessionMilestonesHit }) => {
  const [visibleMessage, setVisibleMessage] = useState(null);
  const [reduceMotion, setReduceMotion] = useState(false);
  const shownMilestones = useRef(new Set());

  // Check for Reduce Motion preference
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduceMotion(mediaQuery.matches);
    
    const handler = (e) => setReduceMotion(e.matches);
    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, []);

  // Determine if we should show a milestone message - ONLY at exact milestones
  useEffect(() => {
    // Only trigger at exactly 3, 9, or 18
    const milestones = [3, 9, 18];
    
    for (const milestone of milestones) {
      if (sessionMilestonesHit.includes(milestone) && 
          !shownMilestones.current.has(milestone)) {
        
        // Mark as shown so we don't repeat
        shownMilestones.current.add(milestone);
        
        const phrase = MILESTONE_PHRASES[milestone];
        setVisibleMessage({ milestone, phrase });
        break; // Only show one at a time
      }
    }
  }, [sessionMilestonesHit]);

  // Auto-hide after 5 seconds
  useEffect(() => {
    if (visibleMessage) {
      const timer = setTimeout(() => {
        setVisibleMessage(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [visibleMessage]);

  if (!visibleMessage) return null;

  // Animation variants - respect Reduce Motion
  const variants = reduceMotion
    ? {
        initial: { opacity: 1 },
        animate: { opacity: 1 },
        exit: { opacity: 0 }
      }
    : {
        initial: { opacity: 0, y: 8 },
        animate: { opacity: 1, y: 0 },
        exit: { opacity: 0, y: -4 }
      };

  return (
    <AnimatePresence mode="wait">
      {visibleMessage && (
        <motion.div
          key={visibleMessage.milestone}
          variants={variants}
          initial="initial"
          animate="animate"
          exit="exit"
          transition={{ duration: reduceMotion ? 0 : 0.6, ease: "easeOut" }}
          className="mb-3 px-1"
          data-testid="completion-acknowledgment"
        >
          <p 
            className="text-sm text-[#78909C] italic text-center"
            style={{ 
              fontWeight: 400,
              letterSpacing: '0.01em'
            }}
          >
            {visibleMessage.phrase}
          </p>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CompletionAcknowledgment;
